/**
 * The firm's material library (0039): reading and editing items, their
 * photos, and copying between the library and a project.
 */

import { supabase } from '@/lib/supabase';
import type { MaterialLibraryRow, SpecRow } from '@/types/database';
import { currentFirm } from '@/lib/current-firm';
import {
  specFromLibrary, libraryFromSpec, libraryPhotoPath, fileNameOf,
} from '@/lib/material-library';

const LIBRARY_BUCKET = 'material-library';
const PROJECT_BUCKET = 'project-media';

type LibraryUpdate = Partial<Omit<MaterialLibraryRow, 'id' | 'firm_id' | 'designer_id' | 'created_at' | 'updated_at'>>;

export async function listLibrary(): Promise<MaterialLibraryRow[]> {
  const { data, error } = await supabase.from('material_library').select('*').order('item');
  if (error) throw error;
  return data ?? [];
}

async function me(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  return user.id;
}

export async function createLibraryItem(fields: LibraryUpdate & { item: string }): Promise<MaterialLibraryRow> {
  const { data, error } = await supabase
    .from('material_library')
    .insert({ ...fields, designer_id: await me() })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateLibraryItem(id: string, patch: LibraryUpdate): Promise<MaterialLibraryRow> {
  const { data, error } = await supabase.from('material_library').update(patch).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

/**
 * Remove an item from the library. Projects that used it keep their copies
 * untouched (0039). Its photo goes too — best effort: a leftover file is
 * harmless, a failed delete of the row is not.
 */
export async function deleteLibraryItem(item: Pick<MaterialLibraryRow, 'id' | 'photo_path'>): Promise<void> {
  const { error } = await supabase.from('material_library').delete().eq('id', item.id);
  if (error) throw error;
  if (item.photo_path) {
    await supabase.storage.from(LIBRARY_BUCKET).remove([item.photo_path]).catch(() => {});
  }
}

function firmId(): string {
  const id = currentFirm()?.id;
  if (!id) throw new Error('Your firm isn’t known yet — reload and try again.');
  return id;
}

export async function uploadLibraryPhoto(file: File): Promise<string> {
  const path = libraryPhotoPath(firmId(), file.name, crypto.randomUUID());
  const { error } = await supabase.storage.from(LIBRARY_BUCKET)
    .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false });
  if (error) throw error;
  return path;
}

const urlCache = new Map<string, { url: string; expires: number }>();

/** A viewable address for a library photo (private bucket). */
export async function libraryPhotoUrl(path: string): Promise<string> {
  const hit = urlCache.get(path);
  if (hit && hit.expires > Date.now() + 30_000) return hit.url;
  const { data, error } = await supabase.storage.from(LIBRARY_BUCKET).createSignedUrl(path, 3600);
  if (error) throw error;
  urlCache.set(path, { url: data.signedUrl, expires: Date.now() + 3_600_000 });
  return data.signedUrl;
}

/**
 * Copy a file between buckets. Supabase copies server-side; if that's refused
 * (older storage server), download and re-upload instead — slower, same result.
 */
async function copyFile(fromBucket: string, fromPath: string, toBucket: string, toPath: string): Promise<void> {
  const { error } = await supabase.storage.from(fromBucket).copy(fromPath, toPath, { destinationBucket: toBucket });
  if (!error) return;
  const { data: blob, error: dlErr } = await supabase.storage.from(fromBucket).download(fromPath);
  if (dlErr || !blob) throw dlErr ?? error;
  const { error: upErr } = await supabase.storage.from(toBucket)
    .upload(toPath, blob, { contentType: blob.type || 'application/octet-stream', upsert: false });
  if (upErr) throw upErr;
}

export interface AddedFromLibrary {
  spec: SpecRow;
  /** False when the item had a photo that couldn't be copied. */
  photoCopied: boolean;
}

/**
 * Add a library item to a room as the room's OWN material. The photo is
 * copied into the project's folder — the client portal signs nothing outside
 * it (0031). If the photo copy fails the material is still added, without it.
 */
export async function addLibraryItemToRoom(
  item: MaterialLibraryRow,
  room: { id: string; project_id: string },
  quantity: number,
  sortOrder: number,
): Promise<AddedFromLibrary> {
  const { data: spec, error } = await supabase
    .from('specs')
    .insert(specFromLibrary(item, { roomId: room.id, quantity, sortOrder }))
    .select()
    .single();
  if (error) throw error;
  if (!item.photo_path) return { spec, photoCopied: true };

  const toPath = `${room.project_id}/rooms/${room.id}/${crypto.randomUUID()}-${fileNameOf(item.photo_path)}`;
  try {
    await copyFile(LIBRARY_BUCKET, item.photo_path, PROJECT_BUCKET, toPath);
    const { data: withPhoto, error: upErr } = await supabase
      .from('specs').update({ thumbnail_url: toPath }).eq('id', spec.id).select().single();
    if (upErr) throw upErr;
    return { spec: withPhoto, photoCopied: true };
  } catch {
    return { spec, photoCopied: false };
  }
}

/**
 * Save a project's material to the firm library: the product only, never its
 * quantity, status or dates. A stored photo is copied into the library's
 * bucket; a pasted web address isn't a stored file, so it's left off.
 */
export async function saveSpecToLibrary(spec: SpecRow): Promise<MaterialLibraryRow> {
  let photo_path: string | null = null;
  if (spec.thumbnail_url && !/^https?:\/\//i.test(spec.thumbnail_url)) {
    const toPath = libraryPhotoPath(firmId(), fileNameOf(spec.thumbnail_url), crypto.randomUUID());
    try {
      await copyFile(PROJECT_BUCKET, spec.thumbnail_url, LIBRARY_BUCKET, toPath);
      photo_path = toPath;
    } catch { /* saved without the photo */ }
  }
  return createLibraryItem({ ...libraryFromSpec(spec), photo_path });
}
